import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BarChart3, Plus, Send, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useConfirm,
  useUndo,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { QuestionsEditor } from '@/features/learning/admin/QuestionsEditor';
import { formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { ProductPicker } from './ProductPicker';
import {
  useProductLists,
  useSpotlightMutations,
  useSpotlightReport,
  useSpotlights,
  type AdminSpotlight,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * "הכר את המוצר" (PRD 5.7.2): מכינים, מוסיפים חידון קצר, ומפרסמים
 * לערוץ.
 *
 * הפרסום הוא פעולה חד כיוונית: הוא כותב הודעה לכל הערוץ. לכן הוא
 * מופרד מהעריכה, נשאל עליו לפני, והכפתור אומר מה יקרה.
 */
export function SpotlightsPage() {
  const confirm = useConfirm();
  const undo = useUndo();
  const canManage = usePerm('products.manage');
  const spotlights = useSpotlights();
  const lists = useProductLists();
  const mutations = useSpotlightMutations();

  const [isAdding, setIsAdding] = useState(false);
  /** המוצר שנבחר בחיפוש. select של כל הקטלוג אינו אפשרות משאלפיים מוצרים. */
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [openReport, setOpenReport] = useState<string | null>(null);
  const [openQuiz, setOpenQuiz] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <AdminPage
      title="הכר את המוצר"
      subtitle="מוצר חדש, הודעה לערוץ, וחידון קצר"
      backTo="/admin/products"
      actions={
        !isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            פרסום חדש
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <Panel title="פרסום חדש">
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const productId = String(form.get('product') ?? '');
              if (!productId) return;
              run(async () => {
                await mutations.create.mutateAsync({
                  product_id: productId,
                  channel_id: String(form.get('channel') ?? '') || null,
                  headline: String(form.get('headline') ?? '').trim() || null,
                  due_date: String(form.get('due') ?? '') || null,
                });
                setIsAdding(false);
                setPicked(null);
              });
            }}
          >
            <div className="grid gap-1">
              <span className="text-body-sm text-muted">המוצר</span>
              {picked ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body text-ink">{picked.name}</span>
                  <Button variant="ghost" onClick={() => setPicked(null)}>
                    החלפת מוצר
                  </Button>
                </div>
              ) : (
                <ProductPicker label="המוצר שעליו מפרסמים" onPick={setPicked} />
              )}
              <input type="hidden" name="product" value={picked?.id ?? ''} />
            </div>

            <div className="grid gap-1">
              <label htmlFor="headline" className="text-body-sm text-muted">
                הכותרת שתופיע בהודעה
              </label>
              <input
                id="headline"
                name="headline"
                placeholder="הכירו את המוצר החדש שלנו"
                className={FIELD}
              />
              <p className="m-0 text-label text-muted">
                התיאור ונקודות המכירה של המוצר נוספים להודעה אוטומטית.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="channel" className="text-body-sm text-muted">
                  לאיזה ערוץ
                </label>
                <select id="channel" name="channel" className={FIELD} required>
                  <option value="">בחירת ערוץ</option>
                  {lists.data?.channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1">
                <label htmlFor="due" className="text-body-sm text-muted">
                  להשלמת החידון עד
                </label>
                <input id="due" name="due" type="date" className={FIELD} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit" disabled={mutations.create.isPending}>
                שמירה כטיוטה
              </Button>
              <Button
                onClick={() => {
                  setIsAdding(false);
                  setPicked(null);
                }}
              >
                ביטול
              </Button>
            </div>
            <p className="m-0 text-body-sm text-muted">
              הטיוטה לא נשלחת לאף אחד. הפרסום לערוץ הוא כפתור נפרד ברשימה.
            </p>
          </form>
        </Panel>
      )}

      {spotlights.isPending && <Skeleton className="h-40" />}
      {spotlights.isError && <ErrorState onRetry={() => void spotlights.refetch()} />}

      {spotlights.data?.length === 0 && !isAdding && (
        <EmptyState
          title="אין עדיין פרסומים"
          description="פרסום מגיע לעובדים כהודעה בערוץ, עם כפתור לחידון."
        />
      )}

      {spotlights.data && spotlights.data.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>מוצר</Th>
              <Th>ערוץ</Th>
              <Th>חידון</Th>
              <Th>מצב</Th>
              <Th>
                <span className="sr-only">פעולות</span>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {spotlights.data.map((spotlight) => (
              <Tr key={spotlight.id}>
                <Td>{spotlight.products?.name ?? '—'}</Td>
                <Td>{spotlight.channels?.name ?? 'לא נבחר'}</Td>
                <Td>
                  {spotlight.quiz_id ? (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setOpenQuiz(openQuiz === spotlight.quiz_id ? null : spotlight.quiz_id)
                      }
                    >
                      {spotlight.quizzes?.title ?? 'חידון'}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        run(() =>
                          mutations.createQuiz.mutateAsync({
                            spotlightId: spotlight.id,
                            title: `חידון ${spotlight.products?.name ?? 'מוצר'}`,
                          }),
                        )
                      }
                    >
                      <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
                      יצירת חידון
                    </Button>
                  )}
                </Td>
                <Td>
                  {spotlight.published_at ? (
                    <StatusBadge tone="good">
                      פורסם {formatDateTime(spotlight.published_at)}
                    </StatusBadge>
                  ) : (
                    <StatusBadge tone="warn">טיוטה</StatusBadge>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {!spotlight.published_at && (
                      <Button
                        variant="primary"
                        disabled={!spotlight.channel_id || mutations.publish.isPending}
                        onClick={() => {
                          const channel = spotlight.channels?.name ?? 'הערוץ';
                          confirm.ask({
                            title: 'פרסום לערוץ',
                            tone: 'warn',
                            body: (
                              <p className="m-0">
                                ההודעה תגיע לכל מי שבערוץ "{channel}", ואי אפשר למחוק אותה
                                אחר כך.
                              </p>
                            ),
                            confirmLabel: 'פרסום בערוץ',
                            onConfirm: () => run(() => mutations.publish.mutateAsync(spotlight.id)),
                          });
                        }}
                      >
                        <Send size={18} strokeWidth={1.75} aria-hidden="true" />
                        פרסום לערוץ
                      </Button>
                    )}
                    {spotlight.published_at && (
                      <Button
                        variant="ghost"
                        aria-label="מעקב"
                        onClick={() =>
                          setOpenReport(openReport === spotlight.id ? null : spotlight.id)
                        }
                      >
                        <BarChart3 size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      aria-label="מחיקה"
                      onClick={() => {
                        undo.remove({
                          id: spotlight.id,
                          message: spotlight.published_at
                            ? 'הפרסום נמחק. ההודעה שנשלחה לערוץ נשארת.'
                            : 'הטיוטה נמחקה',
                          perform: () => mutations.remove.mutateAsync(spotlight.id),
                        });
                      }}
                    >
                      <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {openQuiz && (
        <Panel title="שאלות החידון">
          <QuestionsEditor quizId={openQuiz} />
        </Panel>
      )}

      {openReport && <SpotlightReport spotlightId={openReport} />}
      {confirm.dialog}
    </AdminPage>
  );
}

/** מי ראה ומי ענה (PRD 5.7.3). */
function SpotlightReport({ spotlightId }: { spotlightId: string }) {
  const report = useSpotlightReport(spotlightId);

  if (report.isPending) return <Skeleton className="h-40" />;
  if (report.isError) return <ErrorState onRetry={() => void report.refetch()} />;
  if (!report.data) return null;

  const data = report.data;

  return (
    <Panel title="מי ראה ומי ענה">
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Progress
            value={data.seen}
            max={data.total}
            label={`${data.seen} מתוך ${data.total} פתחו את הערוץ`}
            className="min-w-48 grow"
          />
          {data.has_quiz && (
            <span className="text-body-sm text-muted">
              {data.passed} עברו את החידון
            </span>
          )}
        </div>

        <p className="m-0 text-body-sm text-muted">
          "ראה" נמדד לפי פתיחת הערוץ, וזו לא הוכחה שקרא. הסימן האמיתי הוא מי עשה את החידון.
        </p>

        {data.employees.length === 0 ? (
          <p className="m-0 text-body-sm text-muted">אין עובדים בקהל של הערוץ הזה.</p>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>עובד/ת</Th>
                <Th>סניף</Th>
                <Th>ראה</Th>
                {data.has_quiz && <Th>חידון</Th>}
              </Tr>
            </thead>
            <tbody>
              {data.employees.map((row) => (
                <Tr key={row.employee_id}>
                  <Td>{row.name}</Td>
                  <Td>{row.branch ?? '—'}</Td>
                  <Td>{row.seen ? 'כן' : <span className="text-muted">עוד לא</span>}</Td>
                  {data.has_quiz && (
                    <Td>
                      {row.passed ? (
                        <StatusBadge tone="good">{row.best_score ?? ''}</StatusBadge>
                      ) : row.best_score !== null ? (
                        <StatusBadge tone="bad">{row.best_score}</StatusBadge>
                      ) : (
                        <span className="text-muted">עוד לא ענה</span>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

export type { AdminSpotlight };
